import { type ReactNode, useEffect, useRef } from 'react'

interface Props {
  open: boolean
  onClose: () => void
  children: ReactNode
  size?: 'sm' | 'md' | 'lg' | 'xl' | 'full'
  className?: string
  centered?: boolean
  backdropClassName?: string
}

const sizeClasses: Record<string, string> = {
  sm: 'max-w-sm',
  md: 'max-w-md',
  lg: 'max-w-lg',
  xl: 'max-w-xl',
  full: 'max-w-[min(720px,92vw)]',
}

export function Modal({ open, onClose, children, size = 'md', className = '', centered = false, backdropClassName = '' }: Props) {
  const containerRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!open) return

    function onKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        e.preventDefault()
        onClose()
      }
    }

    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [open, onClose])

  useEffect(() => {
    if (!open) return

    const container = containerRef.current
    if (!container) return

    const focusable = container.querySelector<HTMLElement>(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    )
    focusable?.focus()
  }, [open])

  if (!open) return null

  return (
    <div
      className={`fixed inset-0 z-50 flex justify-center px-4 ${centered ? 'items-center' : 'items-start pt-[18vh]'}`}
      role="dialog"
      aria-modal="true"
      onClick={onClose}
    >
      <div className={`absolute inset-0 backdrop-blur-md animate-fade-in ${backdropClassName || 'bg-cream/30'}`} />
      <div
        ref={containerRef}
        className={`relative w-full ${sizeClasses[size]} bg-ink-raised border border-haze/80 rounded-2xl overflow-hidden animate-scale-in ${className}`}
        style={{ boxShadow: 'var(--shadow-elevated)' }}
        onClick={(e) => e.stopPropagation()}
      >
        {children}
      </div>
    </div>
  )
}
